import { useCallback } from 'react'
import { mutateCachedFetch, useAuth, useCachedFetch } from '@structural-climbing/auth'

export type IdeaKind = 'idea' | 'feedback'
export type IdeaStatus = 'new' | 'review' | 'top' | 'built' | 'declined' | 'read'

export interface MyIdea {
  id: string
  kind: IdeaKind
  body: string
  status: IdeaStatus
  reply: string | null
  createdAt: string
  updatedAt: string
}

export interface IdeasData {
  campaign: { id: string; closesAt: string; open: boolean }
  /** What members see: the starting 17 plus every real idea. */
  count: number
  mine: MyIdea[]
  /** Empty until the campaign closes. */
  winners: { id: string; body: string; status: IdeaStatus }[]
}

export const IDEAS_KEY = 'ideas'

/** Why a send failed, for the toast. */
export type SendError = 'closed' | 'failed'

/**
 * The campaign page's data. Read when the page opens and never on Home: the
 * trophy works from the profile the app already has.
 */
export function useIdeas() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data, loading, error } = useCachedFetch<IdeasData>(
    isAuthenticated ? IDEAS_KEY : null,
    async () => {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/ideas`, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error('Failed to load ideas')
      return (await res.json()) as IdeasData
    },
  )

  /** Sends one idea or piece of feedback; on success it is already on the page. */
  const send = useCallback(
    async (kind: IdeaKind, body: string, gymId: string | null): Promise<MyIdea | SendError> => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/ideas`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind, body, gymId }),
        })
        if (res.status === 409) return 'closed'
        if (!res.ok) return 'failed'
        const { idea } = (await res.json()) as { idea: MyIdea }
        mutateCachedFetch<IdeasData>(IDEAS_KEY, (prev) => ({
          ...prev,
          count: prev.count + (kind === 'idea' ? 1 : 0),
          mine: [idea, ...prev.mine],
        }))
        return idea
      } catch {
        return 'failed'
      }
    },
    [apiUrl, getAccessTokenSilently],
  )

  /** Clears the trophy's dot on the server. Only called when it was set. */
  const markSeen = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${apiUrl}/ideas/seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      // The dot shows once more next time. Nothing else depends on it.
    }
  }, [apiUrl, getAccessTokenSilently])

  return { data: data ?? null, loading, error, send, markSeen }
}
